import { log, warn, logError, debug } from '@/composables/useLogger'
import { useRuntimeConfig } from '#app'

// Extend Nuxt's runtime config to include our logger
declare module '#app' {
  interface NuxtApp {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    log: (...args: unknown[]) => void
    warn: (msg: string, ...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}

// Extend Window interface to include our logger methods
declare global {
  // For browser context only
  interface Window {
    log: (...args: unknown[]) => void
    warn: (...args: unknown[]) => void
    logError: (message: string, error?: unknown, ...args: unknown[]) => void
    debug: (...args: unknown[]) => void
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const isProd = config.public?.isProd ?? false
  const enableLogging = config.public?.enableLogging ?? true
  const logLevel = (config.public?.logLevel ?? 'debug') as string

  // Create production-safe logging functions
  const safeLog = (...args: unknown[]) => {
    // In production, only log if explicitly enabled and not at error level
    if (isProd) {
      if (enableLogging && logLevel !== 'error' && !args[0]?.toString().includes('🎯')) {
        log(...args)
      }
    } else {
      log(...args)
    }
  }

  const safeWarn = (...args: unknown[]) => {
    // In production, only warn if explicitly enabled
    if (isProd) {
      if (enableLogging && !args[0]?.toString().includes('🎯')) {
        warn(...args)
      }
    } else {
      warn(...args)
    }
  }

  const safeDebug = (...args: unknown[]) => {
    // In production, never show debug logs
    if (!isProd) {
      debug(...args)
    }
  }

  // Error logging is always enabled, but filter out non-critical in production
  const safeLogError = (message: string, error?: unknown, ...args: unknown[]) => {
    if (isProd) {
      // In production, only log actual errors and not debug messages marked as errors
      if (error instanceof Error || (typeof message === 'string' && !message.includes('🎯'))) {
        logError(message, error, ...args)
      }
    } else {
      logError(message, error, ...args)
    }
  }

  // Inject into Nuxt context
  nuxtApp.provide('log', safeLog)
  nuxtApp.provide('warn', safeWarn)
  nuxtApp.provide('logError', safeLogError)
  nuxtApp.provide('debug', safeDebug)

  // Add to global scope but with production safeguards
  if (typeof window !== 'undefined') {
    window.log = safeLog
    window.warn = safeWarn
    window.logError = safeLogError
    window.debug = safeDebug
  }
})
